import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";

const CDN = "https://images.squarespace-cdn.com/content/v1/605bdb9a5c6ca56da178ad8a";

const PHOTOS = [
  { path: "58bfee3c-e596-40e4-81aa-cb0899af8851/S+%26+B+-+Tea.jpg", caption: "Tea time together" },
  { path: "27658f89-8f09-4216-a907-27476da8c669/S+%26+B+-+Conversation.jpg", caption: "Stories over coffee" },
  { path: "2899f0a0-8dd7-42d3-8d8e-8052ee2c2e64/Shuffleboard.jpg", caption: "Shuffleboard afternoons" },
  { path: "8cc8fab1-afb5-4cb6-b060-d4a3d33ed332/Plants+2.jpg", caption: "Gardening together" },
  { path: "228a6f74-32f4-42e3-b849-cda3c1534c9d/S+%26+B+-+Chocolates.jpg", caption: "Sweet treats shared" },
  { path: "437c2bbc-13b4-46f5-86f7-a69e132bd36e/Puzzle+1.jpg", caption: "Puzzle day" },
  { path: "8485599e-826f-4ef3-8f6a-610f89f3cda1/Happy+Hour.jpg", caption: "Happy hour" },
  { path: "b343697a-01db-426d-8821-b87b369a1634/Bird+House.jpg", caption: "Building birdhouses" },
  { path: "c3d0f515-fa69-4265-9e29-2dfde57c89c4/Colour+Therapy.jpg", caption: "Colour therapy" },
  { path: "6de6265b-01d6-46bd-8f70-e0fc9884ba58/Cannoli+Time.jpg", caption: "Cannoli time" },
  { path: "8bc217c3-72b1-4539-b27e-7dbd43aa64a1/Easter.jpg", caption: "Easter celebrations" },
  { path: "d663a0da-a16e-407a-90ce-235cedfbab69/Pool+1.jpg", caption: "A friendly game of pool" },
  { path: "1f1dc2c4-ba3c-4a18-a20b-79bcdaafa704/Swen+Christmas+2021.jpg", caption: "A Christmas visit" },
  { path: "1636551979456-3WDPHVBWJ2AXYJAZR9WK/Jessie%2B-%2Bsequin%2Bhorse.jpg", caption: "Sequin art project" },
];

export default function Gallery() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div data-testid="gallery-page">
      <section className="mx-auto max-w-7xl px-4 pb-14 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <p className="eyebrow mb-5" data-testid="gallery-eyebrow">Moments That Matter</p>
        <h1 className="max-w-3xl font-serif text-4xl font-bold leading-[1.1] tracking-tight text-espresso sm:text-5xl lg:text-6xl" data-testid="gallery-heading">
          <MaskedLine delay={0.1}>Real visits, real joy —</MaskedLine>
          <MaskedLine delay={0.25}>
            from <em className="text-brand">our family</em> to yours.
          </MaskedLine>
        </h1>
        <FadeIn delay={0.45}>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-warmgray sm:text-xl">
            A peek at the crafts, games, teas and celebrations our companions share with clients
            across Calgary. Tap any photo to see it up close.
          </p>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="columns-1 gap-6 sm:columns-2 lg:columns-3" data-testid="gallery-grid">
          {PHOTOS.map((photo, i) => (
            <Reveal key={photo.path} delay={(i % 3) * 0.08} className="mb-6 break-inside-avoid">
              <button
                type="button"
                data-testid={`gallery-photo-${i}`}
                onClick={() => setOpenIndex(i)}
                className="group relative block w-full overflow-hidden rounded-3xl border-4 border-white shadow-md shadow-espresso/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-espresso/15 focus:outline-none focus:ring-2 focus:ring-brand"
                aria-label={`View photo: ${photo.caption}`}
              >
                <img
                  src={`${CDN}/${photo.path}?format=750w`}
                  alt={photo.caption}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-espresso/70 to-transparent p-4 pt-10 text-left font-serif text-lg italic text-cream opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  {photo.caption}
                </span>
              </button>
            </Reveal>
          ))}
        </div>

        <FadeIn className="mt-16 text-center">
          <p className="mb-6 font-serif text-2xl font-semibold text-espresso sm:text-3xl">
            Could your loved one use moments like these?
          </p>
          <Link to="/contact" data-testid="gallery-cta-button" className="btn-primary">
            Book a Free Consultation <ArrowRight className="h-5 w-5" />
          </Link>
        </FadeIn>
      </section>

      <Dialog open={openIndex !== null} onOpenChange={(open) => !open && setOpenIndex(null)}>
        <DialogContent className="max-w-3xl border-none bg-transparent p-0 shadow-none" data-testid="gallery-lightbox">
          {openIndex !== null && (
            <div className="relative">
              <img
                src={`${CDN}/${PHOTOS[openIndex].path}?format=1500w`}
                alt={PHOTOS[openIndex].caption}
                className="max-h-[80vh] w-full rounded-2xl object-contain"
                data-testid="gallery-lightbox-image"
              />
              <p className="mt-3 text-center font-serif text-xl italic text-espresso" data-testid="gallery-lightbox-caption">
                {PHOTOS[openIndex].caption}
              </p>
              <button
                type="button"
                data-testid="gallery-lightbox-close"
                onClick={() => setOpenIndex(null)}
                className="absolute -right-2 -top-2 rounded-full bg-espresso p-2.5 text-cream shadow-lg transition-colors hover:bg-brand"
                aria-label="Close photo"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
